import React, { useEffect, useRef, useState } from 'react';
import FilterStage from '../FilterStage/FilterStage';
import useCameraStream from '../../hooks/useCameraStream';
import useHeadPose from '../../hooks/useHeadPose';
import { WIZARD_STEPS, HOLD_MS, MIN_BRIGHTNESS, FACE_MIN_RATIO, FACE_MAX_RATIO } from '../../data/wizardSteps';
import './SkinAnalysisWizard.css';

// Instruction text for each thing that can block the "get into position" gate.
const SETUP_TEXT = {
  loading: 'Preparing face detection…',
  noface: 'Center your face in view',
  far: 'Move a little closer',
  close: 'Move back a little',
  dark: 'Find better, even lighting',
  glasses: 'Please take off your glasses',
};

// Guided, mirrored capture flow: get-into-position gate (face + distance +
// lighting, with a glasses advisory) -> turn left -> turn right -> forward.
// Detected poses auto-advance and silently capture a frame; if detection is
// unavailable or stalls, a manual button keeps the flow moving. Captured frames
// stay in memory and are handed to `onComplete` — they are never uploaded.
const SkinAnalysisWizard = ({ onComplete }) => {
  const { videoRef, ready, captureFrame } = useCameraStream();
  const headPose = useHeadPose({ ready });

  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [faceMissing, setFaceMissing] = useState(false);
  const [manual, setManual] = useState(false);
  const [allowManual, setAllowManual] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [setupIssue, setSetupIssue] = useState('loading'); // what's blocking the setup gate
  const [glasses, setGlasses] = useState(false); // advisory

  const stepIndexRef = useRef(0);
  const baselineRef = useRef(null);
  const holdStartRef = useRef(null);
  const capturesRef = useRef({});
  const brightnessCanvasRef = useRef(null);
  const glassesCanvasRef = useRef(null);
  const completedRef = useRef(false);
  const overlayRef = useRef(null);
  const smoothBoxRef = useRef(null);
  const stepEnteredAtRef = useRef(0);
  const allowManualRef = useRef(false);
  const modelReadyRef = useRef(false);

  // Safety net: if a pose isn't detected within this window, reveal a manual
  // capture button so the user is never stuck.
  const MANUAL_FALLBACK_MS = 4000;

  const measureBrightness = (video) => {
    let c = brightnessCanvasRef.current;
    if (!c) {
      c = document.createElement('canvas');
      c.width = 32;
      c.height = 32;
      brightnessCanvasRef.current = c;
    }
    const ctx = c.getContext('2d', { willReadFrequently: true });
    try {
      ctx.drawImage(video, 0, 0, 32, 32);
    } catch (e) {
      return 0;
    }
    const d = ctx.getImageData(0, 0, 32, 32).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    return sum / (d.length / 4);
  };

  // Best-effort glasses heuristic: sample a strip across the eyes and look for
  // the dark, high-contrast horizontal edges that frames produce. Approximate —
  // used only as an advisory prompt, never to hard-block.
  const detectGlasses = (video, pose) => {
    if (!pose || !pose.keypoints) return false;
    const re = pose.keypoints.find((k) => k.name === 'rightEye') || pose.keypoints[0];
    const le = pose.keypoints.find((k) => k.name === 'leftEye') || pose.keypoints[1];
    if (!re || !le) return false;
    const eyeDist = Math.abs(le.x - re.x) || 1;
    const sx = Math.max(0, Math.floor(Math.min(re.x, le.x) - eyeDist * 0.4));
    const sw = Math.min(video.videoWidth - sx, Math.ceil(Math.abs(le.x - re.x) + eyeDist * 0.8));
    const sh = Math.max(4, Math.ceil(eyeDist * 0.7));
    const sy = Math.max(0, Math.floor((re.y + le.y) / 2 - sh / 2));
    if (sw < 4 || sh < 4) return false;
    let c = glassesCanvasRef.current;
    if (!c) {
      c = document.createElement('canvas');
      glassesCanvasRef.current = c;
    }
    const tw = 48;
    const th = Math.max(8, Math.round((48 * sh) / sw));
    c.width = tw;
    c.height = th;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    try {
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, tw, th);
    } catch (e) {
      return false;
    }
    const d = ctx.getImageData(0, 0, tw, th).data;
    const luma = new Array(tw * th);
    let sum = 0;
    for (let i = 0; i < tw * th; i += 1) {
      const l = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
      luma[i] = l;
      sum += l;
    }
    const mean = sum / (tw * th);
    let dark = 0;
    let grad = 0;
    for (let y = 0; y < th; y += 1) {
      for (let x = 0; x < tw; x += 1) {
        const l = luma[y * tw + x];
        if (l < mean * 0.55) dark += 1;
        if (x > 0) grad += Math.abs(l - luma[y * tw + x - 1]);
      }
    }
    const darkRatio = dark / (tw * th);
    const gradEnergy = grad / (tw * th);
    return darkRatio > 0.22 && gradEnergy > 12;
  };

  const advance = () => {
    holdStartRef.current = null;
    setProgress(0);
    stepEnteredAtRef.current = performance.now();
    allowManualRef.current = false;
    setAllowManual(false);
    const next = stepIndexRef.current + 1;
    if (next >= WIZARD_STEPS.length) {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete(capturesRef.current);
      }
      return;
    }
    stepIndexRef.current = next;
    setStepIndex(next);
  };

  const captureStep = (step, pose) => {
    capturesRef.current[step.id] = { canvas: captureFrame(), pose: pose || null };
  };

  // Draw a rounded outline around the detected head. White when detected,
  // green (filled) when the current target pose is met. The overlay canvas
  // mirrors the video (same intrinsic size + object-fit) so it lines up.
  const drawFaceOverlay = (pose, correct) => {
    const cv = overlayRef.current;
    const video = videoRef.current;
    if (!cv || !video || !video.videoWidth) return;
    if (cv.width !== video.videoWidth) {
      cv.width = video.videoWidth;
      cv.height = video.videoHeight;
    }
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    if (!pose || !pose.faceFound) {
      smoothBoxRef.current = null; // reset so we don't lerp from a stale box
      return;
    }

    // Derive a head box from the detector's face box (preferred) or keypoints.
    let bx;
    let by;
    let bw;
    let bh;
    if (pose.box) {
      bx = pose.box.xMin;
      by = pose.box.yMin;
      bw = pose.box.width != null ? pose.box.width : pose.box.xMax - pose.box.xMin;
      bh = pose.box.height != null ? pose.box.height : pose.box.yMax - pose.box.yMin;
    } else if (pose.keypoints && pose.keypoints.length) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of pose.keypoints) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      bx = minX;
      by = minY;
      bw = maxX - minX;
      bh = maxY - minY;
    } else {
      return;
    }
    // Expand to roughly enclose the whole head.
    const padX = bw * 0.2;
    const padTop = bh * 0.5;
    const padBot = bh * 0.2;
    const target = {
      x: bx - padX,
      y: by - padTop,
      w: bw + padX * 2,
      h: bh + padTop + padBot,
    };

    // Smooth + hysteresis: hold steady for tiny detector jitter, ease toward
    // larger movements so the highlight glides instead of twitching.
    const prev = smoothBoxRef.current;
    let b;
    if (!prev) {
      b = target;
    } else {
      const drift = Math.abs(target.x - prev.x) + Math.abs(target.y - prev.y)
        + Math.abs(target.w - prev.w) + Math.abs(target.h - prev.h);
      if (drift < cv.width * 0.025) {
        b = prev; // within the hysteresis deadzone — don't move
      } else {
        const k = 0.22; // EMA easing
        b = {
          x: prev.x + (target.x - prev.x) * k,
          y: prev.y + (target.y - prev.y) * k,
          w: prev.w + (target.w - prev.w) * k,
          h: prev.h + (target.h - prev.h) * k,
        };
      }
    }
    smoothBoxRef.current = b;

    // Draw an oval highlight.
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    ctx.lineWidth = Math.max(3, cv.width * 0.008);
    ctx.strokeStyle = correct ? '#5CE08A' : 'rgba(255,255,255,0.92)';
    ctx.beginPath();
    ctx.ellipse(cx, cy, b.w / 2, b.h / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
    if (correct) {
      ctx.fillStyle = 'rgba(92,224,138,0.18)';
      ctx.fill();
    }
  };

  // Single RAF loop driving the step state machine. Loop state lives in refs so
  // it never goes stale; React state is only for display.
  useEffect(() => {
    if (!ready) return undefined;
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const video = videoRef.current;
      if (!video || !video.videoWidth || completedRef.current) return;
      if (!stepEnteredAtRef.current) stepEnteredAtRef.current = performance.now();
      if (headPose.getStatus() === 'ready' && !modelReadyRef.current) {
        modelReadyRef.current = true;
        setModelReady(true);
      }
      const step = WIZARD_STEPS[stepIndexRef.current];
      if (!step) return;

      // Detect once per frame; highlight the head and flag a matching pose.
      let pose = null;
      if (modelReadyRef.current) {
        headPose.detect(video);
        pose = headPose.getPose();
      }
      const base = baselineRef.current || { yaw: 0, pitch: 0 };
      const matches = (p) => !!(p && p.faceFound && step.kind === 'pose'
        && step.test({ dyaw: p.yaw - base.yaw, dpitch: p.pitch - base.pitch }));
      // Manual escape if a step stalls (e.g. a glasses false-positive, or
      // detection trouble) — applies to every step.
      if (!allowManualRef.current && performance.now() - stepEnteredAtRef.current > MANUAL_FALLBACK_MS) {
        allowManualRef.current = true;
        setAllowManual(true);
      }

      // Setup gate: face present + good distance + good lighting. Glasses is an
      // advisory that also gently blocks (with the manual escape above).
      if (step.kind === 'setup') {
        const hasFace = !!(pose && pose.faceFound);
        const faceRatio = hasFace && pose.box ? pose.box.width / video.videoWidth : 0;
        const wearingGlasses = hasFace && detectGlasses(video, pose);
        if (glasses !== wearingGlasses) setGlasses(wearingGlasses);

        let issue = null;
        if (!modelReadyRef.current && headPose.getStatus() !== 'unavailable') issue = 'loading';
        else if (!hasFace) issue = 'noface';
        else if (faceRatio && faceRatio < FACE_MIN_RATIO) issue = 'far';
        else if (faceRatio > FACE_MAX_RATIO) issue = 'close';
        else if (measureBrightness(video) < MIN_BRIGHTNESS) issue = 'dark';
        else if (wearingGlasses) issue = 'glasses';
        if (setupIssue !== issue) setSetupIssue(issue);

        drawFaceOverlay(pose, !issue); // green outline once positioned correctly

        if (!issue) {
          if (holdStartRef.current == null) holdStartRef.current = performance.now();
          const held = performance.now() - holdStartRef.current;
          setProgress(Math.min(1, held / HOLD_MS));
          if (held >= HOLD_MS) {
            baselineRef.current = hasFace ? { yaw: pose.yaw, pitch: pose.pitch } : { yaw: 0, pitch: 0 };
            if (headPose.getStatus() === 'unavailable') setManual(true);
            advance();
          }
        } else {
          holdStartRef.current = null;
          setProgress(0);
        }
        return;
      }

      // Pose steps.
      drawFaceOverlay(pose, matches(pose));
      const status = headPose.getStatus();
      if (status === 'unavailable') {
        if (!manual) setManual(true);
        return;
      }
      if (status !== 'ready') return; // still loading the model

      if (!pose || !pose.faceFound) {
        if (!faceMissing) setFaceMissing(true);
        holdStartRef.current = null;
        setProgress(0);
        return;
      }
      if (faceMissing) setFaceMissing(false);
      if (matches(pose)) {
        if (holdStartRef.current == null) holdStartRef.current = performance.now();
        const held = performance.now() - holdStartRef.current;
        setProgress(Math.min(1, held / HOLD_MS));
        if (held >= HOLD_MS) {
          captureStep(step, pose);
          advance();
        }
      } else {
        holdStartRef.current = null;
        setProgress(0);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const manualAdvance = () => {
    const step = WIZARD_STEPS[stepIndexRef.current];
    if (!step) return;
    headPose.detect(videoRef.current);
    const pose = headPose.getPose();
    if (step.kind === 'setup') {
      baselineRef.current = pose && pose.faceFound ? { yaw: pose.yaw, pitch: pose.pitch } : { yaw: 0, pitch: 0 };
      if (headPose.getStatus() === 'unavailable') setManual(true);
      advance();
    } else {
      captureStep(step, pose);
      advance();
    }
  };

  const step = WIZARD_STEPS[stepIndex];
  const total = WIZARD_STEPS.length;
  const displayedInstruction = step && step.kind === 'setup'
    ? (setupIssue ? (SETUP_TEXT[setupIssue] || step.instruction) : 'Perfect — hold still…')
    : step && step.instruction;

  return (
    <FilterStage
      fullBleed
      controlsPlacement="overlay"
      busy={!ready}
      camera={(
        <>
          <video ref={videoRef} className="wizard__video" playsInline muted />
          <canvas ref={overlayRef} className="wizard__overlay" />
        </>
      )}
      controls={
        <div className="wizard__coach">
          <div className="wizard__steps">
            {WIZARD_STEPS.map((s, i) => (
              <span key={s.id} className={`wizard__dot${i < stepIndex ? ' is-done' : ''}${i === stepIndex ? ' is-current' : ''}`} />
            ))}
          </div>
          <div className="wizard__instruction">{displayedInstruction}</div>
          {step?.kind === 'setup' && step?.hint && <div className="wizard__hint">{step.hint}</div>}
          {step?.kind === 'pose' && !modelReady && !manual && (
            <div className="wizard__hint">Preparing face detection…</div>
          )}
          {step?.kind === 'setup' && glasses && setupIssue !== 'glasses' && (
            <div className="wizard__warn">Glasses detected — please remove them</div>
          )}
          {faceMissing && step?.kind === 'pose' && modelReady && !manual && (
            <div className="wizard__warn">Center your face in view</div>
          )}
          {progress > 0 && step?.kind === 'pose' && <div className="wizard__hint wizard__hint--ok">Perfect — hold still…</div>}
          <div className="wizard__hold"><span style={{ transform: `scaleX(${progress})` }} /></div>
          {(manual || allowManual) && (
            <button type="button" className="wizard__capture-btn" onClick={manualAdvance}>
              {step?.kind === 'setup' ? 'Continue anyway' : 'Capture this angle'}
            </button>
          )}
          <div className="wizard__count">{Math.min(stepIndex + 1, total)} / {total}</div>
        </div>
      }
    />
  );
};

export default SkinAnalysisWizard;
